import MetisMenu from "metismenujs";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  changeLayout,
  changeLayoutWidth,
  changeSidebarTheme,
  changeSidebarType,
  changePreloader,
} from "../../store/actions";
import { getRecords } from "../../services/api.service";
import { useEffect, useState } from "react";

const SidebarContent = () => {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const currentPath = useLocation().pathname;

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("authUser"));
        if (!user) {
          return;
        }
        const payload = {
          modelName: "menu_masters",
          schema: "public",
          whereCondition: { is_active: true },
          pagination: { page: 1, pageSize: 1000000 },
          relations: [
            {
              module: "menu_access_masters",
              moduleAs: "menu_access",
              isModuleWhereConditionRequired: true,
            },
          ],
          moduleWhereCondition: {
            role_id: user?.role_id,
            is_active: true,
          },
        };

        const res = await getRecords(payload, "/getRecords");

        if (res?.code === 1) {
          setMenuData(res.data || []);
        }
      } catch (err) {
        console.error("Menu fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMenus();
  }, [localStorage.getItem("menusUpdatedAt")]);

  /* ================= INIT METIS ================= */
  useEffect(() => {
    if (menuData.length) {
      new MetisMenu("#side-menu");
    }
  }, [menuData]);

  /* ================= ACCESS HELPERS ================= */

  const user = JSON.parse(localStorage.getItem("userDetails"));
  const currentRoleId = user?.role_id;

  const hasAccess = (menu) =>
    Array.isArray(menu.menu_access) &&
    menu.menu_access.some((a) => a.access === true);
  const hasChildAccess = (parentId) =>
    menuData.some(
      (m) =>
        m.parent_id === parentId &&
        m.menu_access?.some((a) => a.access === true)
    );

  /* ================= FILTERED MENU ================= */
  const filteredMenuData = menuData.filter(
    (menu) => hasAccess(menu) || hasChildAccess(menu.id)
  );

  const activeClass = ({ isActive }) =>
    isActive ? "active mm-active fw-bold" : "text-dark";
  const hoverStyles = `.hover-smooth {
              transition: font-weight 0.3s ease;
            }

            .hover-smooth:hover {
              span {
                font-weight: 700;

              }
            }`;

  if (loading) {
    return <SidebarSkeleton />;
  }

  return (
    <div id="sidebar-menu">
      <style> {hoverStyles} </style>
      <ul className="metismenu list-unstyled" id="side-menu">
        <li className="menu-title">{t("Menu")}</li>

        {filteredMenuData
          .filter((item) => item.parent_id === null)
          .sort((a, b) => a.sequence - b.sequence)
          .map((parent) => {
            const children = filteredMenuData
              .filter(
                (child) => child.parent_id === parent.id && hasAccess(child)
              )
              .sort((a, b) => a.sequence - b.sequence);

            const isOpen =
              children.some((c) => c.menu_url === currentPath) ||
              parent.menu_url === currentPath;

            /* ===== PARENT WITH CHILD ===== */
            if (children.length > 0) {
              return (
                <li
                  key={parent.id}
                  className={`${isOpen ? "mm-active" : ""} hover-smooth`}
                >
                  <a
                    href="#"
                    className="has-arrow waves-effect"
                    onClick={(e) => e.preventDefault()}
                  >
                    {parent.menu_icon && <i className={parent.menu_icon}></i>}
                    <span
                      className="ms-1 text-dark"
                      style={{ fontWeight: isOpen ? "600" : "" }}
                    >
                      {t(parent.menu_name)}
                    </span>
                  </a>

                  <ul
                    className={
                      isOpen
                        ? "sub-menu mm-collapse mm-show"
                        : "sub-menu mm-collapse"
                    }
                  >
                    {" "}
                    {children.map((child) => (
                      <li key={child.id} className="hover-smooth">
                        <NavLink to={child.menu_url} className={activeClass}>
                          {t(child.menu_name)}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            }

            /* ===== SINGLE MENU ===== */
            return (
              <li key={parent.id} className="hover-smooth">
                <NavLink to={parent.menu_url} className={activeClass}>
                  {parent.menu_icon && <i className={parent.menu_icon}></i>}
                  <span className="ms-1">{t(parent.menu_name)}</span>
                </NavLink>
              </li>
            );
          })}
      </ul>
    </div>
  );
};

const mapStatetoProps = (state) => ({ ...state.Layout });

export default connect(mapStatetoProps, {
  changeLayout,
  changeSidebarTheme,
  changeSidebarType,
  changeLayoutWidth,
  changePreloader,
})(SidebarContent);

const skeletonStyle = {
  backgroundColor: "#f0f0f0",
  borderRadius: "4px",
  position: "relative",
  overflow: "hidden",
  background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.5s infinite linear",
};

const SidebarSkeleton = () => (
  <div id="sidebar-menu">
    <style>
      {`
        @keyframes shimmer {
          to { background-position: -200% 0; }
        }
      `}
    </style>

    <ul className="metismenu list-unstyled" id="side-menu">
      <li className="menu-title" style={{ padding: "12px 20px" }}>
        <div style={{ ...skeletonStyle, width: "80px", height: "18px" }} />
      </li>

      {[...Array(6)].map((_, i) => (
        <li key={i} style={{ padding: "10px 20px" }}>
          <div className="d-flex align-items-center">
            <div
              style={{
                ...skeletonStyle,
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                marginRight: "12px",
              }}
            />
            <div
              style={{
                ...skeletonStyle,
                width: i % 2 === 0 ? "140px" : "110px",
                height: "14px",
              }}
            />
          </div>

          {i % 3 === 0 && (
            <ul style={{ paddingLeft: "34px", marginTop: "8px" }}>
              {[...Array(2)].map((_, j) => (
                <li key={j} style={{ padding: "6px 0" }}>
                  <div
                    style={{
                      ...skeletonStyle,
                      width: "100px",
                      height: "12px",
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  </div>
);
